import { ReactElement } from 'react';
import styled from 'styled-components';
import ForwardArrowIcon from '../../assets/arrow-next-right-icon.svg?react';
import { CheckMark } from '../../util/shared.tsx';
import '../settings_page.css';

// Option metadata consumed by SettingAccordion; `id` lets tutorials spotlight specific option rows.
export interface SettingOption<T> {
  label: string;
  value: T;
  id?: string;
}

interface AccordionProps<T> {
  label: string;
  options: SettingOption<T>[];
  selectedValue: T;
  onSelect: (value: T) => void;
  expanded: boolean;
  onToggle: () => void;
  id?: string;
  explanation?: string;
}

// A setting row whose option list expands in place directly beneath it, rather than
// navigating to a separate picker screen. Tapping an option selects it; the caller
// collapses the row from its onSelect handler.
export function SettingAccordion<T>({
  label,
  options,
  selectedValue,
  onSelect,
  expanded,
  onToggle,
  id,
  explanation,
}: AccordionProps<T>): ReactElement {
  const selectedOption = options.find((option) => option.value === selectedValue);
  return (
    <>
      <div className="setting" id={id} style={{ display: 'flex', alignItems: 'center' }} onClick={onToggle}>
        <div className="settingName">{label}</div>
        <SettingValue>{selectedOption?.label}</SettingValue>
        <Chevron $expanded={expanded} />
      </div>
      <PanelReveal $expanded={expanded}>
        <PanelClip>
          <OptionsPanel>
            {options.map((option) => (
              <OptionRow
                key={option.label}
                id={option.id}
                $selected={option === selectedOption}
                onClick={() => onSelect(option.value)}
              >
                <div>{option.label}</div>
                {option === selectedOption && (
                  <CheckMark $size="22px" $padding="0" $noAutoMargin $fillColor="var(--color-checkmark-song-purple)" />
                )}
              </OptionRow>
            ))}
            {explanation && <Explanation>{explanation}</Explanation>}
          </OptionsPanel>
        </PanelClip>
      </PanelReveal>
    </>
  );
}

interface ToggleProps {
  label: string;
  checked: boolean;
  onToggle: () => void;
  id?: string;
}

// A setting row with an on/off switch that flips in place
export function SettingToggle({ label, checked, onToggle, id }: ToggleProps): ReactElement {
  return (
    <div className="setting" id={id} style={{ display: 'flex', alignItems: 'center' }} onClick={onToggle}>
      <div className="settingName">{label}</div>
      <div className="toggle">
        <input readOnly={true} type="checkbox" checked={checked} />
      </div>
    </div>
  );
}

// values & chevrons stay neutral gray — not the gold --color-text-light
const SettingValue = styled.div`
  color: var(--color-text-darker);
  text-align: right;
  overflow: hidden;
`;

// Down at rest (expands), rotating 180° to up when expanded (collapses). The box is
// square with the gap carried by margins (not padding) so rotation about the center
// cannot eat into the value→chevron gap; matches the link-row chevron in the drawer.
const Chevron = styled(ForwardArrowIcon)<{ $expanded: boolean }>`
  flex: none;
  width: 12px;
  height: 12px;
  margin: 0 10px 0 6px;
  fill: var(--color-text-darker);
  transform: rotate(${({ $expanded }) => ($expanded ? '270deg' : '90deg')});
  transition: transform 200ms ease;
`;

// Grid-row 0fr → 1fr technique animates the panel to/from its natural height;
// the panel stays mounted so collapse animates too (including when opening one
// accordion collapses another). The border lives on OptionsPanel, inside the clip.
const PanelReveal = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $expanded }) => ($expanded ? '1fr' : '0fr')};
  transition: grid-template-rows 200ms ease;
`;

const PanelClip = styled.div`
  overflow: hidden;
  min-height: 0;
`;

const OptionsPanel = styled.div`
  display: flex;
  flex-direction: column;
  margin: 5px 10px 10px;
  border: 1px solid var(--color-divider);
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--color-background);
`;

const OptionRow = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 40px;
  padding: 0 15px;
  cursor: pointer;
  background-color: ${({ $selected }) => ($selected ? 'var(--color-background-selected-item)' : 'transparent')};

  & + & {
    border-top: 1px solid var(--color-divider);
  }
`;

const Explanation = styled.div`
  color: var(--color-text-light);
  font-size: 14px;
  padding: 10px 15px;
  border-top: 1px solid var(--color-divider);
  white-space: normal;
  text-align: left;
`;
